import type { Module } from 'vuex'
import {BundleConfigIds} from "@/types/client";

const LOCAL_STORAGE_KEY = 'voxbet_bundle_template_seen'

interface SeenEntry {
	bundleId: string
	variantId: string
	seenAt: string // ISO date string
}

type SeenContext = SeenEntry[]

export interface BundleTemplateSeenState {
	seen: Record<string, SeenContext>
}

function seenKey(ids: BundleConfigIds): string {
	return `${ids.bundleId}:${ids.variantId}`
}

function getInitialSeen(): Record<string, SeenContext> {
	try {
		const str = localStorage.getItem(LOCAL_STORAGE_KEY)
		return str ? JSON.parse(str) : {}
	} catch {
		return {}
	}
}

function persist(state: BundleTemplateSeenState): void {
	localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state.seen))
}

export const bundleTemplateSeenModule: Module<BundleTemplateSeenState, unknown> = {
	namespaced: true,

	state: () => ({ seen: getInitialSeen() }),

	getters: {
		lastSeenTemplate: (state) => (contextId: string, ids: BundleConfigIds): Date | null => {
			const entry = (state.seen[contextId] ?? []).find(
					(e) => seenKey(e) === seenKey(ids)
			)
			return entry ? new Date(entry.seenAt) : null
		},

		seenFor: (state) => (contextId: string): SeenEntry[] => {
			return [...(state.seen[contextId] ?? [])].sort(
					(a, b) => new Date(b.seenAt).getTime() - new Date(a.seenAt).getTime()
			)
		},
	},

	mutations: {
		MARK_SEEN(state, payload: { contextId: string; ids: BundleConfigIds }): void {
			const { contextId, ids } = payload
			const existing = state.seen[contextId] ?? []

			const filtered = existing.filter((e) => seenKey(e) !== seenKey(ids))
			const updated: SeenEntry[] = [
				{ bundleId: ids.bundleId, variantId: ids.variantId, seenAt: new Date().toISOString() },
				...filtered,
			]

			state.seen = { ...state.seen, [contextId]: updated }
			persist(state)
		},

		CLEAR_CONTEXT(state, contextId: string): void {
			const { [contextId]: _, ...rest } = state.seen
			state.seen = rest
			persist(state)
		},

		CLEAR_ALL(state): void {
			state.seen = {}
			localStorage.removeItem(LOCAL_STORAGE_KEY)
		},
	},

	actions: {
		markTemplateSeen(
				{ commit },
				payload: { contextId: string; bundleConfig: BundleConfigIds }
		): void {
			commit('MARK_SEEN', { contextId: payload.contextId, ids: payload.bundleConfig })
		},

		clearContext({ commit }, contextId: string): void {
			commit('CLEAR_CONTEXT', contextId)
		},

		clearAll({ commit }): void {
			commit('CLEAR_ALL')
		},
	},
}